/**
 * MandateRegistrationPanel.tsx - the Brokermandat registrations of a mandate (Meldung je
 * Versicherungsbeziehung) on the customer screen: one row per insurer with the api's status words and
 * exception chips, and the broker's decisions «Freigeben», «Jetzt melden», «Schliessen», «Erneut
 * melden» and «Meldung auslösen», plus the insurer's answer recorded by a broker: «Bestätigen»,
 * «Ablehnen», «Rückfrage», «Rückfrage beantwortet», «Korrektur» (RegistrationDecisionDialogs), and the
 * reminder loop: «Erinnert am … · Frist 2 bis …», «Klärung seit … · Aufgabe», «Jetzt erinnern» (mails
 * the insurer: communication.send + mandate.eingangskontrolle) and «Eskalieren» (mandate.eingangskontrolle).
 *
 * The api decides which decision a row allows (row.actions) and is the hard line on permissions; a
 * button shows only when the row allows it AND the caller holds every key its route enforces: the
 * routes that mail an insurer need communication.send and mandate.eingangskontrolle, «Schliessen»
 * crm.write, the answer decisions mandate.eingangskontrolle (they mail nobody).
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { formatZurichDateDe, REGISTRATION_JETZT_MELDEN_REASON_VALUES } from '@brokly/shared';
import type { RegistrationDto, RegistrationJetztMeldenReason } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useFollowRegistrationJob, useRegistrationDecision, useRegistrations } from './mandate-registration-queries';
import { RegistrationAnswerForm, isWiedereintragung, type RegistrationAnswerFormKind } from './RegistrationDecisionDialogs';
import { registrationErrorText } from './registration-error-text';
import { RegistrationReminderLines } from './RegistrationReminderLines';

/** The keys of the routes that mail an insurer (route + in-code check). */
export const REGISTRATION_SEND_KEYS = ['communication.send', 'mandate.eingangskontrolle'] as const;
/** The key of «Schliessen». */
export const REGISTRATION_CLOSE_KEY = 'crm.write';
/** The key of the insurer-answer decisions (Bestätigen, Ablehnen, Rückfrage, Rückfrage beantwortet, Korrektur). */
export const REGISTRATION_DECISION_KEY = 'mandate.eingangskontrolle';

const ANSWER_FORM_KINDS: readonly RegistrationAnswerFormKind[] = ['bestaetigen', 'ablehnen', 'rueckfrage', 'korrektur'];
function isAnswerForm(kind: string): kind is RegistrationAnswerFormKind {
  return (ANSWER_FORM_KINDS as readonly string[]).includes(kind);
}

const REASON_LABEL: Record<RegistrationJetztMeldenReason, string> = {
  police_geklaert: 'Police geklärt',
  versicherer_verlangt: 'Versicherer verlangt die Meldung',
  sonstiges: 'Sonstiges',
};

/** A timestamp as the Swiss calendar day (dd.mm.yyyy), never the UTC day. */
function fmtDate(iso: string | null): string | null {
  return iso ? formatZurichDateDe(iso) : null;
}

/** The line on every screen that follows the send job; once the cap stopped the polling, how to see the result. */
export function RegistrationSendingLine({ follow, testId }: { follow: 'live' | 'stopped'; testId: string }) {
  return (
    <p className="text-xs text-muted-foreground" role="status" data-testid={testId}>
      Die Meldung wird im Hintergrund versendet.{' '}
      {follow === 'live'
        ? 'Der Stand aktualisiert sich von selbst.'
        : 'Der Status wird nicht mehr automatisch aktualisiert. Seite neu laden, um den aktuellen Stand zu sehen.'}
    </p>
  );
}

export function MandateRegistrationPanel({
  mandateId,
  subjectType,
  subjectId,
  onlyRegistrationId,
  terminated = false,
  readOnly = false,
}: {
  mandateId: string;
  subjectType: 'person' | 'firma';
  subjectId: string;
  /**
   * One row only, without the heading and «Meldung auslösen»: the expanded insurer of the area «Mandate &
   * Eintragungen» renders its Brokermandat row's decisions through this panel (one permission map).
   */
  onlyRegistrationId?: string;
  /**
   * P6: the Brokermandat is gekündigt (its Mandatsauflösung wirksam): no new Meldung
   * goes out (the api answers 409 termination_live); the existing rows stay workable.
   */
  terminated?: boolean;
  /** P6: the customer is inaktiv (write-protected): the rows only, no decision, Meldung or Schliessen. */
  readOnly?: boolean;
}) {
  const list = useRegistrations(mandateId);
  // While the send job owes a result the list refreshes itself; «Meldung auslösen» starts that watch.
  const sending = useFollowRegistrationJob(mandateId, { subjectType, subjectId });
  const decide = useRegistrationDecision(mandateId, { subjectType, subjectId });
  const { can } = useEffectivePermissions();
  const canSendAny = REGISTRATION_SEND_KEYS.every((k) => can(k)) && !readOnly;
  // «Jetzt erinnern» reminds an insurer of an existing Meldung: allowed during the run-out too.
  const canRemind = canSendAny;
  const canSend = canSendAny && !terminated;
  const canClose = can(REGISTRATION_CLOSE_KEY) && !readOnly;
  const canDecide = can(REGISTRATION_DECISION_KEY) && !readOnly;
  const [open, setOpen] = useState<{ id: string; kind: 'jetzt_melden' | 'schliessen' | 'freigeben_unklar' | RegistrationAnswerFormKind } | null>(null);
  const [reason, setReason] = useState<RegistrationJetztMeldenReason | ''>('');
  const [notiz, setNotiz] = useState('');
  // The «Versand unklar» row whose Freigabe found its mail already sent (and recorded it instead).
  const [alreadySent, setAlreadySent] = useState<string | null>(null);

  function closeForm() {
    setOpen(null);
    setReason('');
    setNotiz('');
  }

  // A failed refetch while polling keeps the rows it already has.
  if (list.isError && list.data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" />
        Die Meldungen an die Versicherer konnten nicht geladen werden.
      </p>
    );
  }
  if (list.data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt …
      </p>
    );
  }
  const rows = onlyRegistrationId !== undefined ? list.data.filter((r) => r.id === onlyRegistrationId) : list.data;

  const renderRow = (r: RegistrationDto) => (
    <li key={r.id} className="flex flex-col gap-1 rounded-md border px-3 py-2 text-sm" data-testid={`reg-row-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{r.carrier_name ?? r.carrier_uid}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs" data-testid="reg-status">
          {r.status_label}
        </span>
        {r.exception_labels.map((label) => (
          <span key={label} className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide">
            {label}
          </span>
        ))}
        {r.overdue && (
          <span className="rounded bg-sem-decide-bg px-1.5 py-0.5 text-xs text-sem-decide" data-testid="reg-overdue">
            überfällig
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        {r.policies.length + r.relationships.length > 0 && (
          <span>
            {r.policies.map((p) => p.policy_number ?? 'ohne Nummer').join(', ')}
            {r.relationships.length > 0 && `${r.policies.length > 0 ? ', ' : ''}${r.relationships.map((x) => x.sparte ?? 'Versicherung').join(', ')}`}
          </span>
        )}
        {fmtDate(r.sent_at) && <span>Gemeldet: {fmtDate(r.sent_at)}</span>}
        {fmtDate(r.response_due_at) && <span>Frist bis: {fmtDate(r.response_due_at)}</span>}
        {r.report_mode_label && <span>Meldung: {r.report_mode_label}</span>}
      </div>
      <RegistrationReminderLines row={r} />
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {canSend && r.actions.freigeben && (
          <Button
            size="sm"
            disabled={decide.isPending}
            onClick={() =>
              // A «Versand unklar» row may have reached the insurer already: ask before mailing again.
              r.exception_codes.includes('versand_unklar')
                ? setOpen({ id: r.id, kind: 'freigeben_unklar' })
                : decide.mutate({ action: 'freigeben', registrationId: r.id })
            }
            data-testid="reg-freigeben"
          >
            Freigeben
          </Button>
        )}
        {canSend && r.actions.jetzt_melden && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ id: r.id, kind: 'jetzt_melden' })} data-testid="reg-jetzt-melden">
            Jetzt melden
          </Button>
        )}
        {canClose && r.actions.schliessen && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ id: r.id, kind: 'schliessen' })} data-testid="reg-schliessen">
            Schliessen
          </Button>
        )}
        {canSend && r.actions.erneut_melden && (
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => decide.mutate({ action: 'erneut_melden', carrierUid: r.carrier_uid })} data-testid="reg-erneut-melden">
            Erneut melden
          </Button>
        )}
        {canRemind && r.actions.erinnern && (
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: 'erinnern', registrationId: r.id })}
            data-testid="reg-erinnern"
          >
            Jetzt erinnern
          </Button>
        )}
        {canDecide && r.actions.eskalieren && (
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: 'eskalieren', registrationId: r.id })}
            data-testid="reg-eskalieren"
          >
            Eskalieren
          </Button>
        )}
        {canDecide && r.actions.versand_bestaetigen && (
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: 'versand_bestaetigt', registrationId: r.id })}
            data-testid="reg-versand-bestaetigt"
          >
            Versand bestätigt
          </Button>
        )}
        {canDecide && r.actions.nicht_versendet && (
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: 'nicht_versendet', registrationId: r.id })}
            data-testid="reg-nicht-versendet"
          >
            Nicht versendet
          </Button>
        )}
        {canDecide && r.actions.bestaetigen && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ id: r.id, kind: 'bestaetigen' })} data-testid="reg-bestaetigen">
            {r.prozess === 'p6_aufloesung' ? 'Austragung bestätigen' : isWiedereintragung(r) ? 'Wiedereintragung bestätigen' : 'Bestätigen'}
          </Button>
        )}
        {canDecide && r.actions.ablehnen && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ id: r.id, kind: 'ablehnen' })} data-testid="reg-ablehnen">
            {/* an insurer refusing the Austragung is a Klärung, never a rejection */}
            {r.prozess === 'p6_aufloesung' ? 'Austragung verweigert' : 'Ablehnen'}
          </Button>
        )}
        {canDecide && r.actions.rueckfrage && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ id: r.id, kind: 'rueckfrage' })} data-testid="reg-rueckfrage">
            Rückfrage
          </Button>
        )}
        {canDecide && r.actions.rueckfrage_beantwortet && (
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: 'rueckfrage_beantwortet', registrationId: r.id, body: {} })}
            data-testid="reg-rueckfrage-beantwortet"
          >
            Rückfrage beantwortet
          </Button>
        )}
        {canDecide && r.actions.korrektur && (
          <Button size="sm" variant="outline" onClick={() => setOpen({ id: r.id, kind: 'korrektur' })} data-testid="reg-korrektur">
            Korrektur
          </Button>
        )}
      </div>
      {r.exception_codes.includes('keine_adresse') && r.actions.freigeben && (
        <p className="text-xs text-muted-foreground" data-testid="reg-hint">
          Ohne Meldeadresse kann nicht gesendet werden: zuerst die Benachrichtigungsadresse beim Versicherer erfassen.
        </p>
      )}
      {r.exception_codes.includes('versand_unklar') && (
        <p className="text-xs text-sem-decide" data-testid="reg-unklar-hint">
          Die Meldung wurde möglicherweise bereits versendet
          {r.unclear_communication_id ? ' (die E-Mail steht in der Kommunikation des Kunden)' : ''}. Bitte vor einer erneuten Freigabe prüfen.
        </p>
      )}
      {alreadySent === r.id && (
        <p className="text-xs text-muted-foreground" role="status" data-testid="reg-already-sent">
          Die Meldung war bereits versendet (im Postfach gefunden) und ist jetzt als gemeldet verbucht. Es wurde nichts erneut gesendet.
        </p>
      )}
      {open?.id === r.id && isAnswerForm(open.kind) && (
        <RegistrationAnswerForm
          key={open.kind}
          kind={open.kind}
          row={r}
          subjectType={subjectType}
          subjectId={subjectId}
          pending={decide.isPending}
          onSubmit={(d) => decide.mutate(d, { onSuccess: closeForm })}
          onCancel={closeForm}
        />
      )}
      {open?.id === r.id && open.kind === 'freigeben_unklar' && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm" data-testid="reg-unklar-confirm-text">
            Die Meldung wurde möglicherweise bereits versendet. Trotzdem erneut senden?
          </span>
          <Button
            size="sm"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate(
                { action: 'freigeben', registrationId: r.id },
                {
                  onSuccess: (res) => {
                    closeForm();
                    if ('already_sent' in res && res.already_sent) setAlreadySent(r.id);
                  },
                },
              )
            }
            data-testid="reg-freigeben-confirm"
          >
            Erneut senden
          </Button>
          <Button size="sm" variant="ghost" onClick={closeForm}>
            Abbrechen
          </Button>
        </div>
      )}
      {open?.id === r.id && open.kind === 'jetzt_melden' && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded border px-2 py-1 text-sm"
            aria-label="Grund"
            value={reason}
            onChange={(e) => setReason(e.target.value as RegistrationJetztMeldenReason | '')}
          >
            <option value="">Grund wählen</option>
            {REGISTRATION_JETZT_MELDEN_REASON_VALUES.map((v) => (
              <option key={v} value={v}>
                {REASON_LABEL[v]}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            disabled={decide.isPending || reason === ''}
            onClick={() => reason !== '' && decide.mutate({ action: 'jetzt_melden', registrationId: r.id, reasonCode: reason }, { onSuccess: closeForm })}
            data-testid="reg-jetzt-melden-confirm"
          >
            Melden
          </Button>
          <Button size="sm" variant="ghost" onClick={closeForm}>
            Abbrechen
          </Button>
        </div>
      )}
      {open?.id === r.id && open.kind === 'schliessen' && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="min-w-48 flex-1 rounded border px-2 py-1 text-sm"
            placeholder="Notiz"
            aria-label="Notiz"
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
          />
          <Button
            size="sm"
            disabled={decide.isPending || notiz.trim() === ''}
            onClick={() => decide.mutate({ action: 'schliessen', registrationId: r.id, notiz }, { onSuccess: closeForm })}
            data-testid="reg-schliessen-confirm"
          >
            Schliessen
          </Button>
          <Button size="sm" variant="ghost" onClick={closeForm}>
            Abbrechen
          </Button>
        </div>
      )}
    </li>
  );

  if (onlyRegistrationId !== undefined) {
    return (
      <div className="flex flex-col gap-2" data-testid="reg-row-embedded">
        {decide.isError && (
          <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
            <AlertTriangle className="size-4" />
            {registrationErrorText(decide.error)}
          </p>
        )}
        <ul className="flex flex-col gap-2">{rows.map(renderRow)}</ul>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-2 rounded-lg border bg-card p-4" aria-label="Meldungen an Versicherer">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Meldungen an Versicherer</h3>
        {canSend && (
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: 'run' })}
            data-testid="reg-run"
          >
            Meldung auslösen
          </Button>
        )}
      </div>
      {terminated && (
        <p className="text-xs text-muted-foreground" data-testid="reg-terminated">
          Das Brokermandat ist gekündigt: keine neuen Meldungen an Versicherer. Bestehende Meldungen bleiben bearbeitbar.
        </p>
      )}
      {decide.isError && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          {registrationErrorText(decide.error)}
        </p>
      )}
      {sending && !decide.isError && (
        // The job runs in the background; the list refreshes itself until it is done or the cap stops it.
        <RegistrationSendingLine follow={sending} testId="reg-sending" />
      )}
      {rows.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Meldung geplant.</p>}
      <ul className="flex flex-col gap-2">{rows.map(renderRow)}</ul>
    </section>
  );
}
